import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FormInput } from "../../components/FormInput";
import FormNumberInput from "../../components/FormNumberInput";
import PanelDataTable from "../../components/PanelDataTable";
import PrimePanel from "../../components/PrimePanel";
import { useEditableFlag } from "../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../store/messageStore";
import {
  F1060203Before,
  queryEquipmentProcessDropdownList,
  queryProductProcessDetail,
  updateProductProcess,
} from "../../utility/service/cbam-service";
import { getCBAMSubItemsByItemNo } from "../../utility/service/companyOffering-service";
import { Base64Encoder, BcryptEncoder, checkDpSuccess } from "../../utility/service/tool-service";
import { addFormValidator } from "../../utility/validate/formValidator";
import { set } from "bignumber.js";

export default function ProductProcessSettingsDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formRule, setFormRule] = useState([]); //表單驗證 設備
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const {
    action,
    setAction,
    itemObj,
    setItemObj,
    selectPlaceList,
    setSelectPlaceList,
    currentCompanyName,
  } = useOutletContext();
  const [, loading] = useContext(SystemLoading);
  // 下拉選單
  const [processList, setProcessList] = useState([]);
  const [processListForProcess, setProcessListForProcess] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const isEditable = useEditableFlag("F10602");
  const [tabAction, setTabAction] = useState("detail");
  const editFlag = tabAction === "detail" || !isEditable;
  const [oriData, setOriData] = useState({});
  const [cols, setCols] = useState(4);
  const selectProcessList = useRef(new Map());

  const {
    getValues,
    setValue,
    watch,
    control,
    register,
    trigger,
    handleSubmit,
    clearErrors,
    setError,
    reset,
    resetField,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {
      offeringsId: "",
      cbamPrecursorsSettingList: [],
      cbamProductProcessLost: [],
    },
    //錯誤驗證時機
    mode: "all",
  });

  // 設置標記，表示從子頁面返回
  useEffect(() => {
    sessionStorage.setItem("fromProductProcessSettingsChildPage", "true");
  }, []);

  useEffect(() => {
    if (action === "detail") {
      const init = async () => {
        await axious_queryEquipmentProcessDropdownList();
        await axios_queryProductProcessDetail();
      };
      axios_F1060203Before();
      init();
    } else {
      btnBackTo();
    }
    setValue("offeringsId", itemObj?.offeringsId);
    axios_getCBAMSubItemsByItemNo("CBAM_PRECURSORS_UNIT", 5).then(setUnitList);
    axios_getCBAMSubItemsByItemNo("CBAM_REDUCING_AGENT", 6).then(setAgentList);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width > 1024) {
        setCols(4);
      } else if (width >= 768) {
        setCols(2);
      } else {
        setCols(1);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const btnBackTo = () => {
    navigate("/management/productProcessSettings");
  };

  const axios_F1060203Before = () => {
    checkLoading(loading);
    F1060203Before()
      .then((res) => {
        if (checkDpSuccess(res)) {
          setFormRule(processConstraints(res?.data?.RespBody?.constraints));
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 製成設備詳情查詢
  const axios_queryProductProcessDetail = () => {
    checkLoading(loading);
    queryProductProcessDetail({
      offeringsId: itemObj?.offeringsId,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          const data = res.data.RespBody;

          data.cbamProductProcessLost.forEach((item, index) => {
            selectProcessList.current.set(index, item.equipmentId);
          });

          // 更新 processList
          setProcessList((prevList) =>
            prevList.map((item) => ({
              ...item,
              disabled: Array.from(selectProcessList.current.values()).includes(item.equipmentId),
            })),
          );

          setOriData(data);
          Object.entries(data).forEach(([key, value]) => {
            setValue(key, value);
          });

          if (data.cbamProductProcessLost.length <= 0) {
            addLostForm(defaultproductProcessLostList);
          } else {
            setValue("cbamProductProcessLost", data.cbamProductProcessLost);
          }

          if (data.cbamPrecursorsSettingList.length <= 0) {
            addSettingForm(defaultPrecursorsSettingList);
          } else {
            setValue("cbamPrecursorsSettingList", data.cbamPrecursorsSettingList);
          }
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  // 製成設備詳情查詢
  const axious_queryEquipmentProcessDropdownList = () => {
    return queryEquipmentProcessDropdownList({ companySeq: selectPlaceList?.companySeq }).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.equipmentProcessList;
        setProcessList(data);
        setProcessListForProcess(data);
      }
    });
  };

  // CBAM 下拉選單
  const axios_getCBAMSubItemsByItemNo = (itemNo, orderNo) => {
    return getCBAMSubItemsByItemNo(itemNo, orderNo).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        return data.map((item, index) => ({
          ...item,
          index,
        }));
      }
      return [];
    });
  };

  const processConstraints = (constraints) => {
    return constraints.map((item) => {
      if (item?.subObject?.value) {
        return { [item?.field]: processConstraints(item?.subObject.value) };
      } else {
        return addFormValidator(item?.field, constraints);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const setValidLostFrom = useCallback(
    (formId) => {
      if (setValidForm("cbamProductProcessLost")?.length > 0) {
        const tmp = setValidForm("cbamProductProcessLost")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamProductProcessLost")],
  );

  const setValidSettingForm = useCallback(
    (formId) => {
      if (setValidForm("cbamPrecursorsSettingList")?.length > 0) {
        const tmp = setValidForm("cbamPrecursorsSettingList")?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [setValidForm("cbamPrecursorsSettingList")],
  );

  const handleResetForm = (fieldArrayApi, defaultList, index) => {
    clearErrors();
    fieldArrayApi.remove(index);
    fieldArrayApi.append(defaultList);
  };

  const handleDelForm = (fieldArrayApi, defaultList, index, length) => {
    clearErrors();
    fieldArrayApi.remove(index);
    if (length <= 1) {
      fieldArrayApi.append(defaultList);
    }
  };

  const defaultproductProcessLostList = {
    productProcessId: "",
  };

  const { fields: productProcessLostFields, append: addLostForm, remove: delLostForm } = useFieldArray({
    control,
    name: "cbamProductProcessLost",
  });

  const lostPanelConfig = {
    header: t("relatedEquipmentProcess"),
  };

  const lostDataTableConfig = {
    dataKey: "id",
    value: productProcessLostFields,
    className: `${editFlag || watch("cbamProductProcessLost.length") >= 10 ? "last-row-no-border" : ""}`,
    footerEvent: () => addLostForm(defaultproductProcessLostList),
    footerDisabled: editFlag || watch("cbamProductProcessLost.length") >= 10,
  };

  const lostColumnsConfig = [
    // 投入製程
    {
      field: "equipmentId",
      fieldName: "equipmentId",
      fieldListName: "cbamProductProcessLost",
      header: t("relatedEquipmentProcess"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      width: "95%",
      rules: setValidLostFrom("equipmentId"),
      options: processList,
      optionLabel: "displayName",
      optionValue: "equipmentId",
      placeholder: t("plz_chs"),
      disabled: editFlag,
      onChange: (rowIndex, newValue) => {
        selectProcessList.current.set(rowIndex, newValue);
        setProcessList((prevList) =>
          prevList.map((item) => ({
            ...item,
            disabled: Array.from(selectProcessList.current.values()).includes(item.equipmentId),
          })),
        );
      },
    },
  ];

  const lostActionConfig = {
    fields: productProcessLostFields,
    fieldId: "id",
    handleDelForm: handleDelForm,
    handleResetForm: handleResetForm,
    fieldArrayApi: { remove: delLostForm, append: addLostForm },
    defaultList: defaultproductProcessLostList,
    disabled: editFlag,
  };

  const defaultPrecursorsSettingList = {
    productPrecursorsId: "",
  };

  const { fields: precursorsSettingFields, append: addSettingForm, remove: delSettingForm } = useFieldArray({
    control,
    name: "cbamPrecursorsSettingList",
  });

  const settingPanelConfig = {
    header: t("precursor"),
  };

  const settingDataTableConfig = {
    dataKey: "id",
    value: precursorsSettingFields,
    className: `${editFlag || watch("cbamPrecursorsSettingList.length") >= 20 ? "last-row-no-border" : ""}`,
    footerEvent: () => addSettingForm(defaultPrecursorsSettingList),
    footerDisabled: editFlag || watch("cbamPrecursorsSettingList.length") >= 20,
  };

  const settingColumnsConfig = [
    // 投入製程
    {
      field: "equipmentId",
      fieldName: "equipmentId",
      fieldListName: "cbamPrecursorsSettingList",
      header: t("inputProcess"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidSettingForm("equipmentId"),
      options: processListForProcess,
      optionLabel: "displayName",
      optionValue: "equipmentId",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 前驅物
    {
      field: "precursorName",
      fieldName: "precursorName",
      fieldListName: "cbamPrecursorsSettingList",
      header: t("precursor"),
      headerRequired: tabAction !== "detail",
      type: "text",
      control: control,
      rules: setValidSettingForm("precursorName"),
      register: register,
      errors: errors,
      disabled: editFlag,
    },
    // 每公噸產品能耗
    {
      field: "precursorsPerTon",
      fieldName: "precursorsPerTon",
      fieldListName: "cbamPrecursorsSettingList",
      header: t("energyPerTonProduct"),
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidSettingForm("precursorsPerTon"),
      disabled: editFlag,
    },
    // 單位
    {
      field: "precursorsUnit",
      fieldName: "precursorsUnit",
      fieldListName: "cbamPrecursorsSettingList",
      header: t("unit"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidSettingForm("precursorsUnit"),
      options: unitList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 主要還原劑
    {
      field: "reducingAgent",
      fieldName: "reducingAgent",
      fieldListName: "cbamPrecursorsSettingList",
      header: t("mainReducingAgent"),
      headerRequired: tabAction !== "detail",
      type: "dropdown",
      control: control,
      rules: setValidSettingForm("reducingAgent"),
      options: agentList,
      optionLabel: "subitemName",
      optionValue: "subitemNo",
      placeholder: t("plz_chs"),
      disabled: editFlag,
    },
    // 直接排放
    {
      field: "seeDirectEmissions",
      fieldName: "seeDirectEmissions",
      fieldListName: "cbamPrecursorsSettingList",
      header: `${t("directEmissions")}(tCO₂e/t)`,
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidSettingForm("seeDirectEmissions"),
      disabled: editFlag,
    },
    // 電力消耗
    {
      field: "seePowerEmissions",
      fieldName: "seePowerEmissions",
      fieldListName: "cbamPrecursorsSettingList",
      header: t("powerEmissions"),
      headerRequired: tabAction !== "detail",
      type: "number",
      control: control,
      rules: setValidSettingForm("seePowerEmissions"),
      disabled: editFlag,
    },
  ];

  const settingActionConfig = {
    fields: precursorsSettingFields,
    fieldId: "id",
    handleDelForm: handleDelForm,
    handleResetForm: handleResetForm,
    fieldArrayApi: { remove: delSettingForm, append: addSettingForm },
    defaultList: defaultPrecursorsSettingList,
    disabled: editFlag,
  };

  const otherSettingFields = [
    { value: "steelPlantIdentification", title: t("steelPlantId"), type: "text" },
    { value: "mnPercent", title: `${t("mnPercent")}%`, type: "number" },
    { value: "crPercent", title: `${t("crPercent")}%`, type: "number" },
    { value: "niPercent", title: `${t("niPercent")}%`, type: "number" },
    { value: "otherMetalPercent", title: `${t("otherMetalPercent")}%`, type: "number" },
    { value: "carbonPercent", title: `${t("carbonPercent")}%`, type: "number" },
    { value: "otherMaterialPercent", title: `${t("otherMaterialPercent")}%`, type: "number" },
    { value: "preConsumerWastePercent", title: `${t("preConsumerWastePercent")}%`, type: "number" },
    { value: "noAlPercent", title: `${t("noAlPercent")}%`, type: "number" },
    { value: "nitricAcidPercent", title: `${t("nitricAcidPercent")}%`, type: "number" },
    { value: "ureaPercent", title: `${t("ureaPercent")}%`, type: "number" },
    { value: "nitrogenPercent", title: `${t("nitrogenPercent")}%`, type: "number" },
    { value: "clinkerIngredients", title: t("clinkerFactor"), type: "number" },
    { value: "concentration", title: t("concentration"), type: "number" },
    { value: "alContainWastePercent", title: `${t("alContainWastePerTon")}(t)`, type: "number" },
    { value: "recyclingScrapSteel", title: `${t("recyclingScrapSteel")}(t)`, type: "number" },
    { value: "ammoniumNPercent", title: `${t("ammoniumNPercent")}%`, type: "number" },
    { value: "nitrateNPercent", title: `${t("nitrateNPercent")}%`, type: "number" },
    { value: "ureaNPercent", title: `${t("ureaNPercent")}%`, type: "number" },
    { value: "otherNPercent", title: `${t("otherNPercent")}%`, type: "number" },
    { value: "isCalcination", title: t("isCalcination"), type: "checkbox" },
  ];

  const renderer = (field) => {
    switch (field.type) {
      case "text":
        return (
          <FormInput
            register={register}
            errors={errors}
            id={field.value}
            name={field.value}
            rules={setValidForm(field.value)}
            className="w-full"
            labelText={field.title}
            labelClass="block truncate w-full"
            disabled={editFlag}
            value={watch(field.value) || ""}
          />
        );
      case "checkbox":
        return (
          <>
            <label className="form-label">{field.title}</label>
            <Controller
              name={`${field.value}`}
              control={control}
              render={({ field: ctrlField }) => (
                <Checkbox
                  disabled={editFlag}
                  checked={watch(field.value) === "Y"}
                  onChange={(e) => ctrlField.onChange(e.checked ? "Y" : "N")}
                />
              )}
            />
          </>
        );
      case "number":
        return (
          <FormNumberInput
            register={register}
            errors={errors}
            id={field.value}
            name={field.value}
            rules={setValidForm(field.value)}
            className="w-full"
            labelText={field.title}
            labelClass="block truncate w-full"
            disabled={editFlag}
            value={watch(field.value)}
            onChange={(newValue) => setValue(field.value, newValue)}
          />
        );
    }
  };

  const encoder = (dataList, code) => {
    const findItemIndex = (map, code) => {
      return map.get(code) ?? false;
    };
    if (code === null || code === undefined || code === "") return "";
    if (dataList === null || dataList === undefined || dataList === "" || dataList.length <= 0) return "";
    return Base64Encoder(BcryptEncoder(code ?? "")) + "," + findItemIndex(dataList, code ?? "");
  };

  const onSubmit = async (data) => {
    checkLoading(loading);

    await new Promise((resolve) => setTimeout(resolve, 0));

    const unitMap = new Map(unitList.map((v, i) => [v.subitemNo, v.index]));
    const agentMap = new Map(agentList.map((v, i) => [v.subitemNo, v.index]));

    await Promise.all(
      data.cbamPrecursorsSettingList.map(async (item) => {
        item.precursorsUnit = encoder(unitMap, item.precursorsUnit);
        item.reducingAgent = encoder(agentMap, item.reducingAgent);
      }),
    );

    updateProductProcess(data)
      .then((res) => {
        if (checkDpSuccess(res)) {
          handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
          btnBackTo();
        }
      })
      .finally(() => {
        checkLoading(loading, false);
      });
  };

  return (
    <>
      {/* 上方基本資訊 */}
      <div className="flex justify-content-between flex-wrap align-items-center">
        <div className="align-items-start">
          <Button
            rounded
            text
            onClick={() => {
              btnBackTo();
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
            {t("button.return_to_list")}
          </Button>
        </div>
        <div className="align-items-end">{t(`button.${action}`)}</div>
      </div>
      {currentCompanyName && (
        <div className="p-card bg-gray">
          <div className="p-card-body">
            <div className="grid">
              <div className="col-12">
                <h3 className="m-0">{currentCompanyName}</h3>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="p-card">
        <div className="p-card-body">
          <form className="grid" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid col-12 lg:col-6">
              {/* 名稱 */}
              <div className="col-12 lg:col-6">
                <FormInput
                  register={register}
                  errors={errors}
                  id="productName"
                  name="productName"
                  type="text"
                  labelText={t("productName")}
                  disabled={true}
                  value={itemObj?.productName}
                />
              </div>
              {/* 英文名稱 */}
              <div className="col-12 lg:col-6">
                <FormInput
                  register={register}
                  errors={errors}
                  id="productEName"
                  name="productEName"
                  type="text"
                  labelText={t("enName")}
                  disabled={true}
                  value={itemObj?.productEName}
                />
              </div>
            </div>

            {/* 相關設備工序 */}
            <div className="col-12">
              <PanelDataTable
                panelConfig={lostPanelConfig}
                dataTableConfig={lostDataTableConfig}
                columnsConfig={lostColumnsConfig}
                actionConfig={lostActionConfig}
              />
            </div>

            {/* 前驅物 */}
            <div className="col-12">
              <PanelDataTable
                panelConfig={settingPanelConfig}
                dataTableConfig={settingDataTableConfig}
                columnsConfig={settingColumnsConfig}
                actionConfig={settingActionConfig}
              />
            </div>

            {/* 其他設定 */}
            <div className="col-12">
              <PrimePanel toggleable className={`evaluation-temp-setting-panel`} header={t("moreSetting")}>
                <div
                  style={{
                    display: "grid",
                    gap: "1rem",
                    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                    padding: "0.5rem",
                  }}
                >
                  {otherSettingFields.map((field) => (
                    <div key={field.value}>{renderer(field)}</div>
                  ))}
                </div>
              </PrimePanel>
            </div>

            <div className="col-12 gap-2 flex-all-center">
              {tabAction === "detail" && (
                <Button
                  disabled={!isEditable}
                  type="button"
                  label={t("button.edit")}
                  size="small"
                  onClick={() => {
                    setTabAction("edit");
                    setAction("update")
                  }}
                />
              )}
              {tabAction === "edit" && (
                <>
                  <Button
                    disabled={!(isValid && isDirty)}
                    type="submit"
                    label={action === "create" ? t("button.create") : t("button.update")}
                    size="small"
                  />
                  {action !== "create" && (
                    <Button
                      outlined
                      size="small"
                      label={t("button.cancel")}
                      onClick={(e) => {
                        e.preventDefault();
                        clearErrors();
                        setTabAction("detail");
                        reset(oriData);
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
